import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { Card } from "react-bootstrap";
import queryString from "query-string";
import { FaExclamationTriangle } from "react-icons/fa";

import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { verifyEmailChangeRequest } from "actions/userActions";
import type { RootState } from "types/state";

import SupportEmailLink from "components/SupportEmailLink";
import IconSpinner from "components/IconSpinner";

const actions = [userConstants.VERIFY_EMAIL_CHANGE];

const isVerifying = createLoadingSelector(actions);
const getErrors = createErrorSelector(actions);
const mapStateToProps = (state: RootState) => {
  return {
    isVerifying: isVerifying(state),
    error: getErrors(state),
    verificationId: queryString.parse(window.location.search).verificationId,
  };
};

const mapDispatchToProps = {
  verifyEmailChangeRequest,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  loginReplaced: boolean;
}

class EmailVerification extends React.PureComponent<Props, State> {
  state: State = { loginReplaced: false };

  componentDidMount() {
    void this.props.verifyEmailChangeRequest(
      this.props.client as ApolloClient<object>,
      this.props.verificationId as string,
      (data) => {
        this.setState({
          loginReplaced: Boolean(data.verifyEmailChange?.loginReplaced),
        });
      }
    );
    window.analytics.page("Individual Email Verification");
  }

  _getErrorContent = () => {
    return (
      <>
        <Card.Title>Email verification failed.</Card.Title>
        <Card.Text>
          We were unable to verify your email. If you believe this message to be
          an error please reach out to <SupportEmailLink />.
        </Card.Text>
      </>
    );
  };

  _getSuccessContent = () => {
    return (
      <>
        <Card.Title>Email verification complete.</Card.Title>
        <Card.Text>
          Your email was successfully verified and may now be used to login as
          well as receive communications from Icon.{" "}
          <a href="/dashboard" className="link">
            Please login
          </a>
          .
        </Card.Text>
      </>
    );
  };
  // the Auth0 account behind a Google sign in cannot change email, so it was replaced, and the replacement
  // has no password on it yet
  _getLoginReplacedContent = () => {
    return (
      <>
        <Card.Title>Email verification complete.</Card.Title>
        <Card.Text>
          Your new email was successfully verified. To finish the change, check
          that inbox for a link to set your password.
        </Card.Text>
      </>
    );
  };

  render() {
    let content;
    if (this.props.isVerifying) {
      content = <IconSpinner centered />;
    } else if (this.props.error) {
      content = this._getErrorContent();
    } else if (this.state.loginReplaced) {
      content = this._getLoginReplacedContent();
    } else {
      content = this._getSuccessContent();
    }
    return (
      <Card className="text-center attention-card">
        {this.props.error && (
          <div className="alert-icon-container">
            <FaExclamationTriangle color="#DB4163" size={42} />
          </div>
        )}
        <Card.Body>{content}</Card.Body>
      </Card>
    );
  }
}

export default connector(withApollo<Props>(EmailVerification));
